// fallow-ignore-file unused-class-member
import { Injectable, inject } from '@angular/core';
import { isEqual } from 'lodash';
import type { CardConfig } from '../models/card-model';
import type { ChartCardConfig } from '../models/chart-config.model';
import {
	ConfigChange,
	ChangeResult,
	ValidationWarning,
	isChartCardConfig,
	isAnalysisCardConfig,
	isTableCardConfig,
} from './card-config-change.types';
import { ChartConfigChangeService } from './chart-config-change.service';
import { TableConfigChangeService } from './table-config-change.service';
import type { TableCardConfig } from '../models/table-config.model';
import { CardPersistenceService } from './card-persistence.service';

/**
 * Tracks which fields were changed in a patch (generic version).
 */
interface PatchChanges {
	cardTypeChanged: boolean;
	dataChanged: boolean;
	visualizationChanged: boolean;
	otherChanged: boolean;
}

/**
 * Single public entry point for **all** card configuration mutations (editors, agents, undo).
 *
 * **Routing (extensible):** `applyChange` dispatches by card shape. Today:
 * - **Chart** (`isChartCardConfig`) → {@link ChartConfigChangeService} (validation, migration, visualization merge) + persistence.
 * - **Table** (`isTableCardConfig`) → {@link TableConfigChangeService}.
 * - **Everything else** → {@link CardConfigChangeService.applyThroughDefaultMergePipeline} (generic shallow/deep merge + persistence).
 *
 * Add new branches in `routeApplyChange` and inject the matching pipeline service—do not grow special cases inside the default merge.
 *
 * `ChartConfigChangeService` is chart-only; callers should inject **this** facade only.
 */
@Injectable({
	providedIn: 'root',
})
export class CardConfigChangeService {
	private chartService = inject(ChartConfigChangeService);
	private tableService = inject(TableConfigChangeService);
	private persistence = inject(CardPersistenceService);

	/** One server revision fetch per cardId per editor session (cleared on cleanup). */
	readonly #serverRevisionRefreshedForCard = new Set<string>();

	/**
	 * Initialize a card for change tracking and persistence.
	 *
	 * @param cardId - Unique card identifier
	 * @param version - Initial server version from loaded card
	 */
	// fallow-ignore-next-line unused-class-member
	initializeCard(cardId: string, version: number | null): void {
		this.persistence.initializeCard(cardId, version);
	}

	/**
	 * Ensures persistence revision is initialized or advanced from loaded card config.
	 * Never downgrades an in-memory revision after a debounced save succeeded.
	 */
	bootstrapPersistenceTracking(
		cardId: string,
		revisions: number | null | undefined
	): void {
		const fromConfig =
			typeof revisions === 'number' && Number.isFinite(revisions)
				? revisions
				: null;
		const tracked = this.persistence.getCurrentRevisions(cardId);
		if (tracked === null) {
			this.persistence.initializeCard(cardId, fromConfig);
			return;
		}
		if (fromConfig !== null && fromConfig > tracked) {
			this.persistence.syncServerRevisions(cardId, fromConfig);
		}
	}

	/**
	 * Seeds persistence from loaded config, then adopts the live server revision
	 * via revision-only `getItem.card` once per cardId until {@link cleanup}.
	 */
	syncPersistenceRevisionOnEditorOpen(
		cardId: string,
		revisions: number | null | undefined
	): void {
		this.bootstrapPersistenceTracking(cardId, revisions);
		const id = cardId?.trim();
		if (!id || this.#serverRevisionRefreshedForCard.has(id)) {
			return;
		}
		this.#serverRevisionRefreshedForCard.add(id);
		void this.refreshServerRevisions(id);
	}

	async refreshServerRevisions(cardId: string): Promise<number | null> {
		return this.persistence.refreshServerRevisions(cardId);
	}

	/**
	 * Applies a configuration change to a card.
	 *
	 * Dispatches via `routeApplyChange`. See class JSDoc for how to add new card-type pipelines.
	 *
	 * @param cardId - Unique card identifier
	 * @param current - Current configuration
	 * @param change - Change to apply
	 * @returns Result with new config and any warnings
	 */
	applyChange(
		cardId: string,
		current: ChartCardConfig,
		change: ConfigChange
	): ChangeResult<ChartCardConfig>;
	applyChange(
		cardId: string,
		current: CardConfig,
		change: ConfigChange
	): ChangeResult;
	applyChange(
		cardId: string,
		current: CardConfig,
		change: ConfigChange
	): ChangeResult {
		return this.routeApplyChange(cardId, current, change);
	}

	/**
	 * Dispatches `applyChange` by card type. Extend here when a card type needs its own
	 * change service (mirror the chart branch: inject service + delegate + typed return).
	 */
	private routeApplyChange(
		cardId: string,
		current: CardConfig,
		change: ConfigChange
	): ChangeResult {
		if (isChartCardConfig(current)) {
			return this.applyThroughChartPipeline(
				cardId,
				current as ChartCardConfig,
				change
			);
		}

		if (isTableCardConfig(current)) {
			return this.applyThroughTablePipeline(cardId, current, change);
		}

		return this.applyThroughDefaultMergePipeline(cardId, current, change);
	}

	private applyThroughTablePipeline(
		cardId: string,
		current: TableCardConfig,
		change: ConfigChange
	): ChangeResult<TableCardConfig> {
		return this.tableService.applyChange(cardId, current, change);
	}

	private applyThroughChartPipeline(
		cardId: string,
		current: ChartCardConfig,
		change: ConfigChange
	): ChangeResult<ChartCardConfig> {
		const chartChange = change as ConfigChange & {
			patch: Partial<ChartCardConfig>;
		};
		return this.chartService.applyChange(
			cardId,
			current,
			chartChange
		) as ChangeResult<ChartCardConfig>;
	}

	/**
	 * Fallback pipeline for card types without a dedicated change service yet.
	 * Shallow merge of the patch, deep merge for `data` and `style`, then `queueChange`.
	 * (Table, markdown, HTML, filter, image, … today.)
	 */
	private applyThroughDefaultMergePipeline(
		cardId: string,
		current: CardConfig,
		change: ConfigChange
	): ChangeResult {
		const changes = this.detectChanges(current, change.patch);

		// Build prospective config (shallow merge first)
		// Use type assertion to handle union type complexity
		let prospective = {
			...current,
			...change.patch,
		} as CardConfig;

		const warnings: ValidationWarning[] = [];

		// Handle data changes with deep merge (only for card types that have data)
		if (changes.dataChanged) {
			const patchData = (
				change.patch as { data?: Record<string, unknown> }
			).data;
			if (patchData) {
				const currentData =
					(current as { data?: Record<string, unknown> }).data ?? {};
				(prospective as { data?: Record<string, unknown> }).data =
					this.deepMerge(currentData, patchData);
			}
		}

		// Handle style changes
		if (change.patch.style) {
			prospective = {
				...prospective,
				style: this.deepMerge(
					(current.style ?? {}) as Record<string, unknown>,
					(change.patch.style ?? {}) as Record<string, unknown>
				) as CardConfig['style'],
			} as CardConfig;
		}

		// Analysis periods persist via `updateAnalysis` on explicit save (jepto app),
		// not debounced `updateCard`. Keep local merge; omit `analyses` from queue.
		const persistencePatch = isAnalysisCardConfig(current)
			? this.#stripAnalysissFromPersistencePatch(change.patch)
			: change.patch;
		if (
			change.persist !== false &&
			Object.keys(persistencePatch).length > 0
		) {
			this.persistence.queueChange(cardId, persistencePatch);
		}

		const trackedRevisions = this.persistence.getCurrentRevisions(cardId);
		if (trackedRevisions !== null) {
			prospective = { ...prospective, revisions: trackedRevisions };
		}

		return {
			config: prospective,
			warnings,
			migrated: false,
		};
	}

	/** Removes `analyses` from patches queued for `updateCard`. */
	#stripAnalysissFromPersistencePatch(
		patch: Partial<CardConfig>
	): Partial<CardConfig> {
		if (!('analyses' in patch)) {
			return patch;
		}
		const { analyses: _analyses, ...rest } =
			patch as Partial<CardConfig> & {
				analyses?: unknown;
			};
		return rest;
	}

	/**
	 * Detects which fields changed in a patch.
	 */
	private detectChanges(
		current: CardConfig,
		patch: Partial<CardConfig>
	): PatchChanges {
		// Safely access data property (not all card types have it)
		const currentData = (current as { data?: unknown }).data;
		const patchData = (patch as { data?: unknown }).data;

		return {
			cardTypeChanged:
				patch.cardType !== undefined &&
				patch.cardType !== current.cardType,
			dataChanged:
				patchData !== undefined && !isEqual(patchData, currentData),
			visualizationChanged: false, // Default-merge path does not use visualization
			otherChanged: Object.keys(patch).some(
				key =>
					!['cardType', 'data', 'visualization'].includes(key) &&
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					!isEqual((patch as any)[key], (current as any)[key])
			),
		};
	}

	/**
	 * Deep merges two objects. Nested objects are recursively merged,
	 * while primitive values from `incoming` take precedence.
	 */
	private deepMerge(
		base: Record<string, unknown>,
		incoming: Record<string, unknown>
	): Record<string, unknown> {
		const result: Record<string, unknown> = { ...base };

		for (const key of Object.keys(incoming)) {
			const baseValue = base[key];
			const incomingValue = incoming[key];

			if (
				baseValue !== null &&
				typeof baseValue === 'object' &&
				!Array.isArray(baseValue) &&
				incomingValue !== null &&
				typeof incomingValue === 'object' &&
				!Array.isArray(incomingValue)
			) {
				// Both are plain objects - recursive merge
				result[key] = this.deepMerge(
					baseValue as Record<string, unknown>,
					incomingValue as Record<string, unknown>
				);
			} else {
				// Primitive, array, or null - incoming takes precedence
				result[key] = incomingValue;
			}
		}

		return result;
	}

	/**
	 * Validates a change without applying it.
	 * For chart cards, delegates to chart service.
	 *
	 * @param current - Current configuration
	 * @param change - Proposed change
	 * @returns Validation warnings only
	 */
	// fallow-ignore-next-line unused-class-member
	previewChange(
		current: CardConfig,
		change: ConfigChange
	): ValidationWarning[] {
		if (isChartCardConfig(current)) {
			const chartChange = change as ConfigChange & {
				patch: Partial<ChartCardConfig>;
			};
			return this.chartService.previewChange(
				current as ChartCardConfig,
				chartChange
			);
		}
		if (isTableCardConfig(current)) {
			return this.tableService.previewChange(
				current as TableCardConfig,
				change
			);
		}
		return [];
	}

	// ═══════════════════════════════════════════════════════════════════════════
	// PERSISTENCE METHODS (work for all card types)
	// ═══════════════════════════════════════════════════════════════════════════

	/**
	 * Force immediate save of pending changes.
	 *
	 * @param cardId - Card identifier
	 * @returns Promise resolving when save completes
	 * @throws When persistence rejects — callers must surface the failure to users.
	 */
	async flush(cardId: string): Promise<void> {
		await this.persistence.flush(cardId);
	}

	/**
	 * Restore a specific historical version (server-side undo/redo).
	 *
	 * @param cardId - Card identifier
	 * @param targetVersion - Version to restore (1 to currentRevisions)
	 * @returns CardConfig with updated revisions
	 */
	// fallow-ignore-next-line unused-class-member
	async restoreVersion(
		cardId: string,
		targetVersion: number
	): Promise<CardConfig> {
		return await this.persistence.restoreVersion(cardId, targetVersion);
	}

	/**
	 * Undo: restore the previous version.
	 * Calculates target version as currentRevisions - 1.
	 *
	 * @param cardId - Card identifier
	 * @returns CardConfig with updated revisions
	 * @throws Error if no history exists (revisions <= 1)
	 */
	async undo(cardId: string): Promise<CardConfig> {
		return await this.persistence.undo(cardId);
	}

	/**
	 * Redo: restore the next snapshot after undo.
	 */
	async redo(cardId: string): Promise<CardConfig> {
		return await this.persistence.redo(cardId);
	}

	canUndo(cardId: string): boolean {
		return this.persistence.canUndo(cardId);
	}

	canRedo(cardId: string): boolean {
		return this.persistence.canRedo(cardId);
	}

	/**
	 * Undo N steps back in history.
	 *
	 * @param cardId - Card identifier
	 * @param steps - Number of steps to undo (must be < currentRevisions)
	 * @returns CardConfig with updated revisions
	 * @throws Error if steps would go before version 1
	 */
	// fallow-ignore-next-line unused-class-member
	async undoSteps(cardId: string, steps: number): Promise<CardConfig> {
		return await this.persistence.undoSteps(cardId, steps);
	}

	/**
	 * Get current server version for a card.
	 *
	 * @param cardId - Card identifier
	 * @returns Current server version or null
	 */
	getCurrentRevisions(cardId: string): number | null {
		return this.persistence.getCurrentRevisions(cardId);
	}

	/**
	 * Check if card has pending unsaved changes.
	 *
	 * @param cardId - Card identifier
	 * @returns Whether there are unsaved changes
	 */
	hasPendingChanges(cardId: string): boolean {
		return this.persistence.hasPendingChanges(cardId);
	}

	/**
	 * Get current persistence status for a card.
	 *
	 * @param cardId - Card identifier
	 * @returns Current status
	 */
	getPersistenceStatus(
		cardId: string
	): 'idle' | 'saving' | 'conflict' | 'offline' | 'error' {
		return this.persistence.getStatus(cardId);
	}

	/**
	 * Subscribe to persistence state changes.
	 *
	 * @param callback - Function to call on state change
	 * @returns Unsubscribe function
	 */
	onPersistenceStateChange(
		callback: (event: {
			cardId: string;
			previousStatus: string;
			currentStatus: string;
			revisions?: number;
		}) => void
	): () => void {
		return this.persistence.onStateChange(event => {
			callback({
				cardId: event.cardId,
				previousStatus: event.previousStatus,
				currentStatus: event.currentStatus,
				revisions: event.revisions,
			});
		});
	}

	/**
	 * Subscribe to successful persistence events.
	 *
	 * @param callback - Function to call when change is persisted
	 * @returns Unsubscribe function
	 */
	onChangePersisted(
		callback: (event: {
			cardId: string;
			revisions: number;
			updatedAt: string;
		}) => void
	): () => void {
		return this.persistence.onChangePersisted(event => {
			callback({
				cardId: event.cardId,
				revisions: event.revisions,
				updatedAt: event.updatedAt,
			});
		});
	}

	onRestoreNavigationChanged(
		callback: (event: { cardId: string }) => void
	): () => void {
		return this.persistence.onRestoreNavigationChanged(callback);
	}

	/**
	 * Clean up persistence state for a card. Call when editor closes.
	 *
	 * @param cardId - Card identifier
	 */
	cleanup(cardId: string): void {
		const id = cardId?.trim();
		if (id) {
			this.#serverRevisionRefreshedForCard.delete(id);
		}
		this.persistence.cleanup(cardId);
	}
}
